import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ArrowLeft, CheckCircle, Clock, Users, FileText, Zap, Star, ChevronDown, ChevronUp } from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
import { InstructorBio } from "./instructor/InstructorBio";
import { CoursesComparisonTable } from "./courses/CoursesComparisonTable";
import { comparisonTableData, scheduleData } from "../data/breeders";
import { ScheduleAndFAQ } from "./schedule/ScheduleAndFAQ";

interface NuestrosCursosPageProps {
  onNavigateBack: () => void;
  onNavigateToCursoOnline: () => void;
  onNavigateToCursoAvanzado: () => void;
  onNavigateToPresencial: () => void;
  onNavigateToNuestrosCursos: () => void;
}

export function NuestrosCursosPage({ 
  onNavigateBack, 
  onNavigateToCursoOnline, 
  onNavigateToCursoAvanzado, 
  onNavigateToPresencial,
  onNavigateToNuestrosCursos
}: NuestrosCursosPageProps) {
  const { t } = useTranslation();

  const cursos = [
    {
      id: 'introduccion',
      title: t("courses.introduction.title"),
      subtitle: t("courses.introduction.subtitle"),
      description: t("courses.introduction.description"),
      image: '/assets/meristemo.jpeg',
      bullets: [
        'Técnicas básicas y enfoque práctico para conservar y sanear tus genéticas',
        '5 horas de contenido en directo con grabación y PDF incluidos',
        'Monta tu propio laboratorio con certificado incluido'
      ],
      bgColor: 'from-black to-gray-900',
      textColor: 'text-white',
      accentColor: 'text-orange-500',
      buttonBg: 'bg-orange-500 hover:bg-orange-600',
      onClick: onNavigateToCursoOnline,
      nivel: t("courses.introduction.level"),
      duracion: t("courses.introduction.duration"),
      formato: t("courses.introduction.format"),
      precio: t("courses.introduction.price")
    },
    {
      id: 'avanzado',
      title: t("courses.advanced.title"),
      subtitle: t("courses.advanced.subtitle"),
      description: t("courses.advanced.heroDescription"),
      image: '/assets/nodal.png',
      bullets: [
        'Preparación de medios, semillas sintéticas y desinfección de genéticas',
        'Técnicas avanzadas de micropropagación y eliminación de HLVd',
        'Grabación del curso y PDF con certificado de finalización'
      ],
      bgColor: 'from-black to-gray-900',
      textColor: 'text-white',
      accentColor: 'text-orange-500',
      buttonBg: 'bg-orange-500 hover:bg-orange-600',
      onClick: onNavigateToCursoAvanzado,
      nivel: t("courses.advanced.level"),
      duracion: t("courses.advanced.duration"),
      formato: t("courses.advanced.format"),
      precio: t("courses.advanced.price")
    },
    {
      id: 'presencial',
      title: t("courses.presencial.title"),
      subtitle: t("courses.presencial.subtitle"),
      description: t("courses.presencial.description"),
      image: '/assets/presencial.png',
      bullets: [
        'Trabajo directo con equipos de vanguardia y materiales reales',
        '3 días intensivos en laboratorio profesional con grupos reducidos',
        'Certificación internacional con práctica hands-on completa'
      ],
      bgColor: 'from-black to-gray-900',
      textColor: 'text-white',
      accentColor: 'text-orange-500',
      buttonBg: 'bg-orange-500 hover:bg-orange-600',
      onClick: onNavigateToPresencial,
      nivel: t("courses.presencial.level"),
      duracion: t("courses.presencial.duration"),
      formato: t("courses.presencial.format"),
      precio: t("courses.presencial.price")
    }
  ];


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onNavigateBack}
              className="flex items-center text-gray-600 hover:text-orange-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>{t("common.back")}</span>
            </button>
            
            <div className="flex items-center">
              <img src={rebelLogo} alt="Rebel Genomics" className="w-8 h-8 mr-2" />
              <span className="text-xl text-gray-800">RebelGenomics</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl mb-6"
          >
            {t("courses.comparison.title")}
            <br />
            <span className="text-black">{t("courses.comparison.compare")}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
          >
            {t("courses.comparison.subtitle")}
          </motion.p>
        </div>
      </section>

      {/* Cursos Section - Mejorada */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {cursos.map((curso, index) => (
              <motion.div
                key={curso.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <Card className={`h-full bg-gradient-to-br ${curso.bgColor} ${curso.textColor} overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border-0`}>
                  {/* Badge de nivel */}
                  <div className="absolute top-4 left-1 z-10">
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${curso.accentColor} bg-white/20 backdrop-blur-sm border border-white/30`}>
                      {curso.nivel}
                    </span>
                  </div>
                  
                  {/* Badge de precio */}
                  <div className="absolute top-4 right-4 z-10">
                    <span className="px-4 py-2 rounded-full text-lg font-bold bg-white/20 backdrop-blur-sm border border-white/30">
                      {curso.precio}
                      </span>
                    </div>
                    
                  {/* Imagen principal */}
                  <div className="relative h-64 overflow-hidden">
                    <ImageWithFallback
                      src={curso.image}
                      alt={curso.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    
                    {/* Overlay con información */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{curso.duracion}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>{curso.formato}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenido del card */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold mb-2">
                      {curso.title}
                    </h3>
                    <p className="text-lg mb-4 opacity-90">
                      {curso.subtitle}
                    </p>
                    <p className="mb-6 opacity-80 leading-relaxed text-sm">
                      {curso.description}
                    </p>

                    {/* Bullets mejorados */}
                    <div className="space-y-3 mb-6">
                      {curso.bullets.map((bullet, bulletIndex) => (
                        <div key={bulletIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm opacity-90">{bullet}</span>
                        </div>
                      ))}
                    </div>

                    {/* Rating y alumnos */}
                    <div className="flex items-center justify-between mb-6 text-sm">
                        <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 mr-1" />
                        <span className="font-semibold">4.9/5</span>
                        <span className="ml-2 opacity-70">(+100 alumnos)</span>
                        </div>
                        <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-1" />
                        <span>{t("courses.presencial.certificate")}</span>
                    </div>
                  </div>

                    {/* Botón CTA */}
                    <Button
                      onClick={curso.onClick}
                      className={`w-full ${curso.buttonBg} text-white font-semibold py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105 text-lg`}
                    >
{t("courses.comparison.viewDetails")}
                    </Button>
                      </div>
                    </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Instructor Bio Section */}
      <InstructorBio />

{/* Tabla Comparativa - Bloque 3 */}
      <section
        id="comparativa"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              {t("courses.comparison.compare")}
              <br />
              <span className="text-black">
                {t("courses.comparison.title")}
              </span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <CoursesComparisonTable 
              columns={comparisonTableData.columns}
              rows={comparisonTableData.rows}
              notes={comparisonTableData.notes}
              onEnroll={(courseKey) => {
                if (courseKey === 'onlineInitiation') {
                  onNavigateToCursoOnline();
                } else if (courseKey === 'onlineAdvanced') {
                  onNavigateToCursoAvanzado();
                } else if (courseKey === 'presencial') {
                  onNavigateToPresencial();
                }
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* Schedule and FAQ Section */}
      <ScheduleAndFAQ 
        dates={scheduleData.dates} 
        faq={scheduleData.faq} 
        onCta={(courseId) => {
          if (courseId === 'online-introduccion') {
            onNavigateToCursoOnline();
          } else if (courseId === 'online-avanzado') {
            onNavigateToCursoAvanzado();
          } else if (courseId === 'presencial') {
            onNavigateToPresencial();
          }
        }} 
      />

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl mb-6">
              {t("courses.comparison.readyToStart1")}
              <br />
              <span className="text-black">{t("courses.comparison.readyToStart2")}</span>
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t("courses.comparison.joinProfessionals")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="bg-white hover:bg-gray-100 text-orange-500 text-xl px-12 py-6 rounded-full transition-all duration-300 hover:scale-105"
                onClick={onNavigateToCursoOnline}
              >
{t("courses.comparison.startWithIntroduction")}
              </Button>
              <Button
                size="lg"
                className="bg-black text-white hover:bg-gray-800 hover:text-white text-xl px-12 py-6 rounded-full"
                onClick={() => window.open("https://calendly.com/pedro-cifuentes-rebelgenomics/30min", "_blank")}
              >
{t("courses.comparison.personalizedConsultation")}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
// Añadir el componente X que faltaba
function X({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

// Añadir el componente Check que faltaba
function Check({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M20 4L9 15L4 10" />
    </svg>
  );
}